import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";

import BankAccount from "assets/images/bankOne.jpg";
import BankAccount2 from "assets/images/bank2.png";
import BankAccount3 from "assets/images/bank3.png";
import BankAccount4 from "assets/images/bank4.png";
import BankAccount5 from "assets/images/bank5.jpg";

import AppButton from "components/button";

import { getPaymentIntent, getPaymentOptions } from "api/Payment";

import useApi from "hooks/useApi";
import { useTranslation } from "i18n";

import "./payment.css";

const HIDDEN_PAYMENT_KEYS = new Set(["banContact", "creditCard"]);

const BANKS = [
  { id: "bankOne", name: "De Surinaamsche Bank" },
  { id: "bankTwo", name: "Hakrinbank" },
  { id: "bankThree", name: "Republic Bank Suriname" },
  { id: "bankFour", name: "Finabank" },
  { id: "bankFive", name: "Banco di Caribe (Curaçao)" },
];

function payment({
  setPaymentType,
  paymentType,
  totalPrice,
  tikkieUrl,
  totalAmount,
  setCharge,
  id,
  currency,
  handleOnSuccessPaymentRequest,
  onlyTikkiePayment,
  referenceId,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPaymentOptions = useApi(getPaymentOptions);

  useEffect(() => {
    const currencyFromLocalStorage = JSON.parse(
      localStorage.getItem("satyam-currency"),
    )?.currency;

    fetchPaymentOptions.request({
      currency: currencyFromLocalStorage,
    });
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: 24,
    width: { xs: "90%", md: "550px" },
    p: 4,
    "&:focus": {
      outline: "none",
    },
  };

  const openInNewTab = () => {
    if (tikkieUrl) {
      window.open(tikkieUrl, "_blank")?.focus();
    }
  };

  const visiblePaymentOptions = (
    fetchPaymentOptions?.data?.paymentOptions || []
  ).filter((option) => !HIDDEN_PAYMENT_KEYS.has(option?.key));

  const selectedOption = visiblePaymentOptions.find(
    (option) => option.key === paymentType,
  );
  const extraCharge =
    selectedOption?.charges * 1 > 0
      ? ((selectedOption.charges * 1 * totalPrice) / 100).toFixed(2)
      : null;

  const methodHint = (key) => {
    if (key === "cash") return t("payment.cashHint");
    return t("payment.methodHint");
  };

  const handlePayment = async () => {
    setLoading(true);
    const body = {
      currency: currency?.currency,
      totalAmount,
      flightReservationId: id,
      paymentType:
        typeof paymentType === "string"
          ? paymentType
          : fetchPaymentOptions?.data?.paymentOptions[paymentType]?.key,
    };
    const response = await getPaymentIntent({
      body,
    });

    if (!response.ok) {
      setLoading(false);
      return;
    } else {
      handleOnSuccessPaymentRequest(
        response?.data?.paymentIntent?.client_secret,
      );
      setLoading(false);
    }
  };

  const selectMethod = (nextType, charges, onSelect) => {
    if (charges != null) {
      setCharge?.(+charges);
    }
    setPaymentType(nextType);
    onSelect?.();
  };

  const reference = referenceId || t("payment.yourReference");

  return (
    <>
      <div className="pay">
        <header className="pay-head">
          <p className="pay-kicker">{t("payment.title")}</p>
          <h2>{t("payment.completeTitle")}</h2>
          <p>{t("payment.lead")}</p>
        </header>

        {fetchPaymentOptions?.loading ? (
          <p className="pay-loading">{t("payment.loading")}</p>
        ) : (
          <>
            <p className="pay-label">{t("payment.chooseMethod")}</p>
            <div className="pay-methods">
              {currency?.currency === "EUR" && (
                <button
                  type="button"
                  className={`pay-method${paymentType === 4 ? " is-selected" : ""}`}
                  onClick={() => selectMethod(4, 0, openInNewTab)}
                >
                  <span className="pay-method-radio" aria-hidden="true" />
                  <span className="pay-method-copy">
                    <strong>{t("payment.tikkie")}</strong>
                    <span>{t("payment.tikkieHint")}</span>
                  </span>
                </button>
              )}

              {!onlyTikkiePayment &&
                currency?.currency !== "SRD" &&
                visiblePaymentOptions.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    className={`pay-method${
                      paymentType === option.key ? " is-selected" : ""
                    }`}
                    onClick={() => selectMethod(option.key, option.charges)}
                  >
                    <span className="pay-method-radio" aria-hidden="true" />
                    <span className="pay-method-copy">
                      <strong>{option.value}</strong>
                      <span>{methodHint(option.key)}</span>
                    </span>
                  </button>
                ))}

              {!onlyTikkiePayment && (
                <button
                  type="button"
                  className={`pay-method${paymentType === 3 ? " is-selected" : ""}`}
                  onClick={() => selectMethod(3, 0)}
                >
                  <span className="pay-method-radio" aria-hidden="true" />
                  <span className="pay-method-copy">
                    <strong>{t("payment.bankTransfers")}</strong>
                    <span>{t("payment.bankHint")}</span>
                  </span>
                </button>
              )}
            </div>

            {extraCharge && paymentType !== "cash" && (
              <p className="pay-fee">
                {t("payment.extraCharge")}: {currency?.sign}
                {extraCharge}
              </p>
            )}

            {paymentType === "cash" && (
              <div className="pay-next">
                <p>{t("payment.cashNextTitle")}</p>
                <span>
                  {t("payment.cashNext", { reference })}
                </span>
                <div className="pay-offices">
                  <Link to="/contact-us">
                    {t("helpDesk.offices.suriname.location")}
                  </Link>
                  <Link to="/contact-us">
                    {t("helpDesk.offices.netherlands.country")}
                  </Link>
                  <Link to="/contact-us">
                    {t("helpDesk.offices.curacao.country")}
                  </Link>
                </div>
              </div>
            )}

            {paymentType === 3 && (
              <div className="pay-next">
                <p>{t("payment.bankNextTitle")}</p>
                <span>{t("payment.bankNext", { reference })}</span>
                <div className="pay-banks">
                  <p>{t("payment.chooseBank")}</p>
                  <div className="pay-bank-list">
                    {BANKS.map((bank) => (
                      <button
                        type="button"
                        key={bank.id}
                        onClick={() => setOpen(bank.id)}
                      >
                        {bank.name}
                        <span>{t("payment.viewDetails")}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <a
                  className="pay-whatsapp"
                  href={`https://wa.me/597436950?text=${encodeURIComponent(
                    t("payment.whatsappProofMessage", { reference }),
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("payment.sendProofWhatsapp")}
                </a>
              </div>
            )}

            <aside className="pay-other">
              <p>{t("payment.otherMethodsTitle")}</p>
              <span>
                {t("payment.otherMethods", { reference })}{" "}
                <Link to="/contact-us">{t("payment.contactUs")}</Link>
              </span>
            </aside>
          </>
        )}
      </div>

      {!onlyTikkiePayment &&
        currency?.currency !== "SRD" &&
        paymentType !== undefined &&
        paymentType !== 3 &&
        paymentType !== 4 &&
        paymentType !== "cash" && (
          <AppButton
            className="primary-color w-100 bigBtn"
            onClick={handlePayment}
            loading={loading}
          >
            {t("payment.payNow")}
          </AppButton>
        )}
      <Modal
        open={!!open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {open === "bankOne" && (
            <>
              <Box textAlign="center">
                <img src={BankAccount} alt="" />
              </Box>
              <Box my={2} textAlign="center">
                <p className="medium-font-size">DE SURINAAMSCHE BANK</p>
                <p className="medium-font-size">SATYAM HOLIDAYS N.V.</p>
              </Box>
              <p className="medium-font-size">SRD 3299708</p>
              <p className="medium-font-size">USD 3299716</p>
              <p className="medium-font-size">EUR 3299724</p>
              <p className="medium-font-size">Paramaribo -Suriname S.A.</p>
            </>
          )}
          {open === "bankTwo" && (
            <>
              <Box textAlign="center">
                <img src={BankAccount2} alt="" />
              </Box>
              <Box my={2} textAlign="center">
                <p className="medium-font-size">HAKRINBANK N.V.</p>
                <p className="medium-font-size">SATYAM HOLIDAYS N.V.</p>
              </Box>
              <p className="medium-font-size">SRD 20.912.88.32</p>
              <p className="medium-font-size">USD 20.818.94.39</p>
              <p className="medium-font-size">EUR 20.818.94.47</p>
              <p className="medium-font-size">Paramaribo -Suriname S.A.</p>
            </>
          )}
          {open === "bankThree" && (
            <>
              <Box textAlign="center">
                <img src={BankAccount3} alt="" />
              </Box>
              <Box my={2} textAlign="center">
                <p className="medium-font-size">REPUBLIC BANK SURINAME</p>
                <p className="medium-font-size">SATYAM HOLIDAYS N.V.</p>
              </Box>
              <p className="medium-font-size">SRD 5.00.000.9568</p>
              <p className="medium-font-size">USD 5.00.000.9576</p>
              <p className="medium-font-size">EUR 5.00.000.9584</p>
              <p className="medium-font-size">Paramaribo -Suriname S.A.</p>
            </>
          )}
          {open === "bankFour" && (
            <>
              <Box textAlign="center">
                <img src={BankAccount4} alt="" />
              </Box>
              <Box my={2} textAlign="center">
                <p className="medium-font-size">FINA BANK SURINAME</p>
                <p className="medium-font-size">SATYAM HOLIDAYS N.V.</p>
              </Box>
              <p className="medium-font-size">SRD 1.2076339</p>
              <p className="medium-font-size">USD 1.2076354</p>
              <p className="medium-font-size">EUR 1.2076347</p>
              <p className="medium-font-size">Paramaribo -Suriname S.A.</p>
            </>
          )}
          {open === "bankFive" && (
            <>
              <Box textAlign="center">
                <img src={BankAccount5} alt="" />
              </Box>
              <Box my={2} textAlign="center">
                <p className="medium-font-size">Banco di Caribe</p>
                <p className="medium-font-size">SATYAM HOLIDAYS N.V.</p>
              </Box>
              <p className="medium-font-size">Schottegatweg Oost 205 </p>
              <p className="medium-font-size">Willemstad, Curaçao</p>
              <p className="medium-font-size">Account nbr. 334736.01 (ANG)</p>
              <p className="medium-font-size">Account nbr. 334736.02 (USD) </p>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
}

export default payment;
